import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ArrowRight, Download, MapPin, CodeXml, ShieldCheck, ChartColumn, Folder, GraduationCap, Star,
} from 'lucide-react';
import './HeroSection.css';
import './HeroSection.motion.css';
import useFitScale from './hooks/useFitScale';
import usePointerParallax from './hooks/usePointerParallax';
import useDockMagnify from './hooks/useDockMagnify';
import useRotatingIndex from './hooks/useRotatingIndex';
import { CONTACT, SOCIALS } from './profile';
import { trackEvent } from './analytics';

import Portrait from './Portrait';
// Inline SVGs (not <img>): decorative blobs must never become the page's LCP element
import { ReactComponent as Blob1 } from './images/design/hero-blob-1.svg';
import { ReactComponent as Blob2 } from './images/design/hero-blob-2.svg';
import { ReactComponent as Blob3 } from './images/design/hero-blob-3.svg';
import dash1 from './images/design/hero-dash-1.svg';
import dash2 from './images/design/hero-dash-2.svg';
import dot1 from './images/design/hero-dot-1.svg';
import dot2 from './images/design/hero-dot-2.svg';
import arrowDoodle from './images/design/arrow-doodle.svg';
import logoReact from './images/design/logo-react.svg';
import logoAngular from './images/design/logo-angular.svg';
import logoSpringBoot from './images/design/logo-spring-boot.svg';
import logoKotlin from './images/design/logo-kotlin.svg';
import logoFlutter from './images/design/logo-flutter.svg';
import logoPython from './images/design/logo-python.svg';
import awsSmile from './images/design/logo-aws-smile.svg';

const TECH_LOGOS = [
    { name: 'Spring Boot', src: logoSpringBoot },
    { name: 'Kotlin', src: logoKotlin },
    { name: 'Flutter', src: logoFlutter },
    { name: 'Angular', src: logoAngular },
    { name: 'React', src: logoReact },
    { name: 'Python', src: logoPython },
];

const HEADLINE_INTERVAL = 3500;

// Headlines are stacked in place; the <h1> takes the height of the ACTIVE one (measured, then
// transitioned), so the paragraph always follows the real headline height with the normal flex gap.
const RotatingHeadline = () => {
    const { t, i18n } = useTranslation();
    const headlines = useMemo(() => {
        const rest = t('hero.title_rotation', { returnObjects: true });
        return [
            { l1: t('hero.title_l1'), l2: t('hero.title_l2'), accent: t('hero.title_accent') },
            ...(Array.isArray(rest) ? rest : []),
        ];
    }, [t, i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

    const active = useRotatingIndex(headlines.length, HEADLINE_INTERVAL);
    const previous = useRef(active);
    const leaving = previous.current !== active ? previous.current : null;
    useEffect(() => { previous.current = active; }, [active]);

    const titleRef = useRef(null);
    const itemRefs = useRef([]);

    // Size the <h1> to the active headline; re-measure whenever its wrapping changes (resize, fonts)
    useLayoutEffect(() => {
        const title = titleRef.current;
        const item = itemRefs.current[active];
        if (!title || !item) return undefined;

        const apply = () => { title.style.height = `${item.offsetHeight}px`; };
        apply();
        title.classList.add('is-measured');

        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(apply);
        observer.observe(item);
        return () => observer.disconnect();
    }, [active, headlines]);

    return (
        <h1 className="hero-title hero-headline" ref={titleRef}>
            {headlines.map(({ l1, l2, accent }, i) => {
                const state = i === active ? 'is-active' : i === leaving ? 'is-leaving' : '';
                return (
                    <span
                        key={i}
                        ref={el => { itemRefs.current[i] = el; }}
                        className={`headline-item ${state}`}
                        aria-hidden={i !== active}
                    >
                        <span className="title-line"><span className="ink">{l1}</span></span>
                        <span className="title-line">
                            <span className="ink">
                                {l2 ? `${l2} ` : null}<span className="gradient-text">{accent}</span>
                            </span>
                        </span>
                    </span>
                );
            })}
        </h1>
    );
};

const VISUAL_W = 600;
const VISUAL_H = 450;

const HeroVisual = () => {
    const { t } = useTranslation();
    const [ref, scale] = useFitScale(VISUAL_W);
    const dockRef = useRef(null);
    useDockMagnify(dockRef);

    return (
        <div className="hero-visual" ref={ref} style={{ height: VISUAL_H * scale }}>
            <div className="hero-artboard" dir="ltr" style={{ transform: `scale(${scale})` }}>
                <Blob1 aria-hidden="true" className="deco hl hl--glow-1" style={{ left: 50, top: 9, width: 600, height: 441 }} />
                <Blob2 aria-hidden="true" className="deco hl hl--glow-2" style={{ left: 300, top: 117, width: 372, height: 326 }} />
                <Blob3 aria-hidden="true" className="deco hl hl--glow-3" style={{ left: 78, top: 54, width: 252, height: 230 }} />
                <img src={dash1} alt="" className="abs hl hl--orbit-1" style={{ left: 130, top: 99, width: 70, height: 230 }} />
                <img src={dash2} alt="" className="abs hl hl--orbit-2" style={{ left: 480, top: 225, width: 80, height: 160 }} />

                <Portrait
                    priority
                    sizes="(max-width: 640px) 260px, 400px"
                    className="abs hl hl--portrait"
                    style={{ left: 130.83, top: 0, width: 398.35, height: 450 }}
                />

                <img src={dot1} alt="" className="abs hl hl--dot-1" style={{ left: 115, top: 99, width: 11, height: 11 }} />
                <img src={dot2} alt="" className="abs hl hl--dot-2" style={{ left: 535, top: 279, width: 11, height: 11 }} />

                <div className="abs hl hl--code code-badge" style={{ left: 158, top: 149.5 }}>
                    <CodeXml size={24} />
                </div>

                <div className="abs hl hl--card-clean float-card" style={{ left: 20, top: 18 }}>
                    <span className="icon-tile" style={{ background: 'var(--brand-soft)', color: 'var(--brand)' }}>
                        <ShieldCheck size={20} />
                    </span>
                    <span className="float-card-text">
                        <strong>{t('hero.card_clean')}</strong>
                        <small>{t('hero.card_clean_l1')}</small>
                        <small>{t('hero.card_clean_l2')}</small>
                    </span>
                </div>

                <div className="abs hl hl--card-years float-card float-card--years" style={{ left: 420, top: 162 }}>
                    <span className="icon-tile years-tile">
                        <ChartColumn size={20} />
                    </span>
                    <span className="float-card-text">
                        <strong className="years-value">{t('hero.years')}</strong>
                        <small>{t('hero.years_sub')}</small>
                    </span>
                </div>

                <p className="abs hl hl--note hand-note" style={{ left: 488, top: 18 }}>
                    {t('hero.doodle', { returnObjects: true }).map(line => <span key={line}>{line}</span>)}
                </p>
                <img src={arrowDoodle} alt="" className="abs hl hl--note" style={{ left: 446, top: 68, width: 56, height: 48 }} />

                {/* Anchored to the right edge so the wider stack stays inside the artboard */}
                <div className="abs hl hl--dock tech-stack" style={{ right: 0, top: 386 }} ref={dockRef}>
                    {TECH_LOGOS.map(({ name, src }) => (
                        <img key={name} src={src} alt={name} title={name} width="26" height="26" className="dock-item" />
                    ))}
                    <span className="aws-logo dock-item" title="AWS">
                        aws
                        <img src={awsSmile} alt="" width="24" height="6.6" />
                    </span>
                    <a href="#Services" className="tech-more dock-item" aria-label={t('hero.more_tech')}>•••</a>
                </div>
            </div>
        </div>
    );
};

const HeroSection = ({ projectCount }) => {
    const { t } = useTranslation();
    const sectionRef = useRef(null);
    usePointerParallax(sectionRef);

    const stats = [
        { value: `${projectCount}+`, label: t('hero.stats.projects'), Icon: Folder, bg: '#eaf1ff', fg: '#2563eb' },
        { value: '4', label: t('hero.stats.internships'), Icon: GraduationCap, bg: '#f1ecff', fg: '#7c3aed' },
        { value: '100%', label: t('hero.stats.satisfaction'), Icon: Star, bg: '#fff4e1', fg: '#f59e0b' },
    ];

    return (
        <section className="hero" id="Home" ref={sectionRef}>
            <div className="container hero-content">
                <div className="hero-text">
                    <div className="hero-badges hero-enter" style={{ '--enter-delay': '60ms' }}>
                        <span className="badge badge--available">
                            <span className="badge-dot" />
                            {t('hero.badge_open')}
                        </span>
                        <span className="badge badge--location">
                            <MapPin size={13} color="#ef4444" />
                            {t('hero.badge_location')}
                        </span>
                    </div>

                    <p className="hero-eyebrow hero-enter" style={{ '--enter-delay': '120ms' }}>{t('hero.eyebrow')}</p>

                    <RotatingHeadline />

                    <p className="hero-intro hero-enter" style={{ '--enter-delay': '300ms' }}>{t('hero.intro')}</p>

                    <div className="hero-buttons hero-enter" style={{ '--enter-delay': '360ms' }}>
                        <a href={CONTACT.cv} download className="btn btn-primary" onClick={() => trackEvent('CV Download')}>
                            <Download size={18} />
                            {t('hero.download_cv')}
                            <ArrowRight size={16} className="flip-rtl" />
                        </a>
                        <a href="#Contact" className="btn btn-secondary" onClick={() => trackEvent('CTA Click', { button: 'lets_talk' })}>
                            {t('hero.lets_talk')}
                            <ArrowRight size={16} className="flip-rtl" />
                        </a>
                    </div>

                    <div className="follow hero-enter" style={{ '--enter-delay': '420ms' }}>
                        <span className="follow-label">{t('hero.follow')}</span>
                        {SOCIALS.map(({ key, label, href, Icon }) => (
                            <a
                                key={key}
                                href={href}
                                className="social-square"
                                onClick={() => trackEvent('Social Click', { network: key, location: 'hero' })}
                                aria-label={label}
                                title={label}
                                target={key === 'mail' ? undefined : '_blank'}
                                rel="noopener noreferrer"
                            >
                                <Icon size={17} />
                            </a>
                        ))}
                    </div>
                </div>

                <HeroVisual />
            </div>

            <div className="container hero-stats hero-enter" style={{ '--enter-delay': '480ms' }}>
                {stats.map(({ value, label, Icon, bg, fg }) => (
                    <div key={label} className="stat-card">
                        <span className="icon-tile" style={{ background: bg, color: fg }}>
                            <Icon size={20} />
                        </span>
                        <span className="stat-text">
                            <strong>{value}</strong>
                            <small>{label}</small>
                        </span>
                    </div>
                ))}
            </div>
        </section>
    );
};

export default HeroSection;
